import { z } from 'zod'
import { FilterEntityFieldsSchema } from './filter-entity'

// ─── Aggregate Functions ───

export const AggregateFunction = z.enum([
  'AVG',
  'CNT',
  'CNT_PCT',
  'MAX',
  'MIN',
  'SUM',
  'MED',
  'SD_POP',
  // Distribution shape beyond the median, and distinct values (e.g. tickers vs
  // ticker-days). Added 2026-09-17 with the matching ettalchemy aggregates so a
  // panel can render what Deep Research queries. Keep in step with
  // packages/ettalchemy/ettalchemy/queries/enums/functions.py `Aggregates`.
  'P05',
  'P10',
  'P25',
  'P75',
  'P90',
  'P95',
  'CNT_DST',
])
export type AggregateFunction = z.infer<typeof AggregateFunction>

export const AGGREGATE_SHORT_LABELS: Record<AggregateFunction, string> = {
  AVG: 'AVG',
  CNT: 'CNT',
  CNT_PCT: 'CNT%',
  MAX: 'MAX',
  MIN: 'MIN',
  SUM: 'SUM',
  MED: 'MED',
  SD_POP: 'SD',
  P05: 'P5',
  P10: 'P10',
  P25: 'P25',
  P75: 'P75',
  P90: 'P90',
  P95: 'P95',
  CNT_DST: 'DST',
}

export const AGGREGATE_LONG_LABELS: Record<AggregateFunction, string> = {
  AVG: 'Average',
  CNT: 'Count',
  CNT_PCT: 'Count %',
  MAX: 'Maximum',
  MIN: 'Minimum',
  SUM: 'Sum',
  MED: 'Median',
  SD_POP: 'Std Dev',
  P05: '5th percentile',
  P10: '10th percentile',
  P25: '25th percentile',
  P75: '75th percentile',
  P90: '90th percentile',
  P95: '95th percentile',
  CNT_DST: 'Distinct count',
}

/**
 * Title-case function prefix auto-prepended to an aggregate metric's display
 * name (see {@link aggregateDisplayLabel}), so every reader knows which
 * aggregation they're looking at.
 */
export const AGGREGATE_PREFIX_LABELS: Record<AggregateFunction, string> = {
  AVG: 'Avg',
  CNT: 'Cnt',
  CNT_PCT: 'Cnt %',
  MAX: 'Max',
  MIN: 'Min',
  SUM: 'Sum',
  MED: 'Med',
  SD_POP: 'SD',
  P05: 'P5',
  P10: 'P10',
  P25: 'P25',
  P75: 'P75',
  P90: 'P90',
  P95: 'P95',
  CNT_DST: 'Distinct',
}

// ─── Aggregate Target ───
//
// Mirrors the universal query-leaf shape `{ type, column?, value? }` used by
// filter rights. Currently only `type: 'column'` is wired up, but keeping the
// discriminator preserves room for a future `{ type: 'value', value: 1 }`
// target (constant aggregates, CNT_PCT etc. that aren't column-derived) and
// for swap-back-with-memory UX where stale `value`/`column` siblings stick
// around when the user flips the type.

export const AggregateTargetType = z.enum(['column'])
export type AggregateTargetType = z.infer<typeof AggregateTargetType>

export const AggregateTarget = z.object({
  column: z.string().nullable(),
  type: AggregateTargetType,
})
export type AggregateTarget = z.infer<typeof AggregateTarget>

// ─── Metric Axis ───

/** Left is the default value axis; right is the secondary one. Max two. */
export const MetricAxis = z.enum(['left', 'right'])
export type MetricAxis = z.infer<typeof MetricAxis>

// ─── Aggregate Entity ───

export const AggregateEntitySchema = z.object({
  id: z.string(),
  conditionalAgg: AggregateFunction,
  label: z.string(),
  description: z.string(),
  target: AggregateTarget,
  conditions: z.array(FilterEntityFieldsSchema),
  /**
   * A query-shaped clause, sent as one more condition alongside the flat list.
   *
   * `conditions` is a list of leaves and so can only say AND. The backend takes
   * `list[QueryFilter | FilterClause]`, which means a whole `{ AND | OR }` tree
   * is one legal element, and a directional outcome needs exactly that: "closed
   * back through the prior close" is one branch per gap direction.
   *
   * ADDITIVE on purpose. The aggregate form edits `conditions` and never has to
   * learn about a tree, every stored profile stays valid, and the percentage's
   * denominator is unchanged because the backend collects the columns a clause
   * names the same way it collects a leaf's.
   *
   * Backend operator tokens (`GE`, `LT`), like group-by cases: it is passed
   * through untouched rather than translated from the form vocabulary.
   */
  when: z.record(z.unknown()).optional(),
  chartType: z.enum(['line', 'bar']).optional(),
  showAsPercent: z.boolean().optional(),
  /**
   * Which value axis this metric plots on in the time-series / bar charts.
   * Unset = auto (see `resolveMetricAxes` in history/hooks/chart-value-axes):
   * the first metric's unit owns the left axis and a metric with a different
   * unit goes right. Only two axes exist, ever.
   */
  axis: MetricAxis.optional(),
})
export type AggregateEntity = z.infer<typeof AggregateEntitySchema>

// ─── Aggregate Group (for visual grouping in the table) ───

export const AggregateGroupSchema = z.object({
  label: z.string(),
  data: z.array(z.string()),
})
export type AggregateGroup = z.infer<typeof AggregateGroupSchema>

// ─── Aggregate Profile ───

export const AggregateProfileSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  aggregates: z.array(AggregateEntitySchema),
  groups: z.array(AggregateGroupSchema).optional(),
})
export type AggregateProfile = z.infer<typeof AggregateProfileSchema>

// ─── Aggregate Definition (derived for rendering) ───

export interface AggregateDef {
  formatter: (value: unknown) => string
  label: string
  name: string
  original: string | null
  conditionalAgg: AggregateFunction
}

// ─── Helpers ───

let _nextAggId = 1

export function createAggregateId(): string {
  return `agg_${_nextAggId++}`
}

export function resetAggregateIdCounter(start = 1): void {
  _nextAggId = start
}

/**
 * Display name for an aggregate metric: the short aggregation, then the label
 * ("Gap %" as AVG reads "Avg Gap %"). We add the aggregation; the label never
 * carries it, and neither may a preset name (aggregate-presets.unit.test.ts
 * enforces that). A blank label shows the aggregation alone ("Avg", or "Cnt %"
 * for a show-as-% count) so a bar is never nameless.
 *
 * This is a DISPLAY transform only; the stored `label` is never mutated, and
 * badge sites (which already show the function beside the label) use the label.
 */
export function aggregateDisplayLabel(
  agg: Pick<AggregateEntity, 'conditionalAgg' | 'label' | 'showAsPercent'>,
): string {
  const fn: AggregateFunction =
    agg.showAsPercent && agg.conditionalAgg === 'CNT' ? 'CNT_PCT' : agg.conditionalAgg
  const prefix = AGGREGATE_PREFIX_LABELS[fn]
  const base = agg.label?.trim()
  return base ? `${prefix} ${base}` : prefix
}

/** Returns true if the aggregate function requires a target column. */
export function aggRequiresColumn(fn: AggregateFunction): boolean {
  // CNT and CNT_PCT count sessions meeting the conditions — no target column.
  return fn !== 'CNT' && fn !== 'CNT_PCT'
}

export function createDefaultAggregate(): AggregateEntity {
  return {
    id: createAggregateId(),
    conditionalAgg: 'AVG',
    label: '',
    description: '',
    target: { column: null, type: 'column' },
    conditions: [],
  }
}
